import { alpha, Box, Container, Stack, Typography } from "@mui/material"

export default function CitationStudyQ12025() {
    return (
        <Box
            id="hero"
            sx={(theme) => ({
                width: '100%',
                backgroundImage:
                    theme.palette.mode === 'light'
                        ? 'linear-gradient(180deg, #CEE5FD, #FFF)'
                        : `linear-gradient(#02294F, ${alpha('#090E10', 0.0)})`,
                backgroundSize: '100% 20%',
                backgroundRepeat: 'no-repeat',
            })}
        >
            <Container
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    pt: { xs: 14, sm: 20 },
                    pb: { xs: 8, sm: 12 },
                }}
            >
                <Stack spacing={2} useFlexGap sx={{ width: { xs: '100%', sm: '100%' } }}>
                    <Typography
                        component="span"
                        variant="h2"
                        sx={{
                            fontSize: 'clamp(1.4rem, 10vw, 1.4rem)',
                            color: (theme) =>
                                theme.palette.mode === 'light' ? 'primary.dark' : 'primary.contrastText',
                        }}
                    >
                        21st May 2025
                    </Typography>
                    <Typography component="span"
                        variant="h1"
                        sx={{
                            fontSize: '2rem',
                            color: (theme) =>
                                theme.palette.mode === 'light' ? 'primary.main' : 'primary.light',
                        }}>
                        Citing Retracted Research: What Q1 2025 Tells Us About Citation Quality
                    </Typography>
                    <img src="/static/images/blog/citation_study_q1_2025_scitility_argos_retractions.png" alt="Bar chart representing the position of publisher imprints in term of ratio of articles that contain a retracted reference" />
                    <Typography textAlign="left"
                        color="text.secondary"
                        sx={{ alignSelf: 'left', width: { sm: '100%' }, fontSize: 'clamp(1.5rem, 10vw, 1.5rem)' }}>
                        Analyzing articles published in the first quarter of 2025 reveals a crucial insight: a non-negligible share of published research continues to cite retracted papers, even if those retractions are documented prior to the publication of the article.
                    </Typography>
                    <Typography textAlign="left"
                        color="text.secondary"
                        sx={{ alignSelf: 'left', width: { sm: '100%' }, fontSize: 'clamp(1.5rem, 10vw, 1.5rem)' }}>
                        Our data, covering a wide range of publishing imprints, each of which has over 2,400 articles published during the quarter, shows a significant variation in the percentage of articles that included at least one retracted article in their references. The industry average across these publishers is approximately 1.315%, but looking at the table clearly shows some imprints perform very well while others show room for improvement.
                    </Typography>
                    <Typography variant="h2"
                        sx={{
                            mt: 2,
                            mb: 0,
                            fontSize: '1.5rem',
                            color: (theme) =>
                                theme.palette.mode === 'light' ? 'primary.main' : 'primary.light',
                        }}>
                        What the Data Shows
                    </Typography>
                    <Typography textAlign="left"
                        color="text.secondary"
                        sx={{
                            alignSelf: 'left', width: { sm: '100%' }, fontSize: 'clamp(1.5rem, 10vw, 1.5rem)'
                        }}>
                        <ul>
                            <li>The data covering a wide spectrum of scholarly publishers has retraction citation rates ranging from below 0.2% to above 4%.</li>
                            <li>There were over 9k articles published with known retractions in the references in Q1 alone.</li>
                            <li>The variation may indicate different levels of capability and technical infrastructure across the industry.</li>
                        </ul>
                    </Typography>
                    <Typography variant="h2"
                        sx={{
                            mt: 2,
                            mb: 0,
                            fontSize: '1.5rem',
                            color: (theme) =>
                                theme.palette.mode === 'light' ? 'primary.main' : 'primary.light',
                        }}>
                        Why Retractions Still Get Cited?
                    </Typography>
                    <Typography textAlign="left"
                        color="text.secondary"
                        sx={{
                            alignSelf: 'left', width: { sm: '100%' }, fontSize: 'clamp(1.5rem, 10vw, 1.5rem)'
                        }}>
                        There are several reasons why retracted papers might still appear in citations:
                        <ul>
                            <li>Authors may be unaware that a reference has been retracted, especially if databases or citation tools don’t flag them.</li>
                            <li>Reference lists may be compiled using older datasets or static bibliographies.</li>
                            <li>In some cases, citations to retracted articles are deliberate (e.g., to discuss the retraction itself). Note that this is rarely the case for the bulk of incidental references and we worked hard in the above chart to remove the obvious cases.</li>
                            <li>Publishers are screening references using old or out of date databases.</li>
                        </ul>
                        Regardless of intent, the outcome is the same: retracted research re-enters the scientific record, risking the spread of misinformation and undermining scholarly trust.
                    </Typography>
                    <Typography variant="h2"
                        sx={{
                            mt: 2,
                            mb: 0,
                            fontSize: '1.5rem',
                            color: (theme) =>
                                theme.palette.mode === 'light' ? 'primary.main' : 'primary.light',
                        }}>
                        How Scitility Can Help
                    </Typography>
                    <Typography textAlign="left"
                        color="text.secondary"
                        sx={{ alignSelf: 'left', width: { sm: '100%' }, fontSize: 'clamp(1.5rem, 10vw, 1.5rem)' }}>
                        Manually checking every reference for retraction status is not feasible at scale, especially given the sheer volume of scholarly output. This is where tools like the Scitility Reference API can make a real and measurable difference.

                        The <a href="https://scitility.github.io/argos-api-doc-public/?#tag/reference-analysis" target="_blank">Scitility Reference API</a> allows publishers, editorial platforms, and even individual researchers to:
                        <ol>
                            <li>Have access to <a href="https://www.scitility.com/argos" target="_blank">the most complete source of retractions.</a></li>
                            <li>Automatically scan reference lists for retracted publications.</li>
                            <li>Cross-reference with our authoritative retraction data set in real time.</li>
                            <li>Integrate into editorial workflows to flag issues before an article goes to print.</li>
                        </ol>
                    </Typography>
                    <Typography textAlign="left"
                        color="text.secondary"
                        sx={{ alignSelf: 'left', width: { sm: '100%' }, fontSize: 'clamp(1.5rem, 10vw, 1.5rem)' }}>
                        By using scalable and automated solutions like this, the publishing community can drastically reduce the risk of retracted literature slipping through the cracks.
                    </Typography>
                    <Typography textAlign="left"
                        color="text.secondary"
                        sx={{ alignSelf: 'left', width: { sm: '100%' }, fontSize: 'clamp(1.5rem, 10vw, 1.5rem)' }}>
                        In a publishing environment that aspires to scientific excellence and integrity, ensuring that citations are up-to-date and valid is no longer optional, it's essential.
                    </Typography>
                </Stack>
            </Container></Box>
    )
}